import { useState, useEffect, useCallback, useRef } from 'react';

interface InactivityTimerProps {
  onIdle: () => void;
  onPrompt: () => void;
  isLoggedIn: boolean;
  promptBeforeIdle?: number;
  idleTimeout?: number;
}

const useInactivityTimer = ({
  onIdle,
  onPrompt,
  isLoggedIn,
  promptBeforeIdle = 60 * 1000, // 1 minute
  idleTimeout = 15 * 60 * 1000, // 15 minutes
}: InactivityTimerProps) => {
  const [isPrompted, setIsPrompted] = useState(false);
  // FIX: The return type of `setTimeout` in the browser is `number`, not `NodeJS.Timeout`.
  // Initializing with `null` also resolves the `useRef` argument error.
  const promptTimer = useRef<number | null>(null);
  const idleTimer = useRef<number | null>(null);

  const startTimers = useCallback(() => {
    promptTimer.current = window.setTimeout(() => {
      onPrompt();
      setIsPrompted(true);
    }, idleTimeout - promptBeforeIdle);

    idleTimer.current = window.setTimeout(() => {
      onIdle();
      setIsPrompted(false);
    }, idleTimeout);
  }, [idleTimeout, promptBeforeIdle, onPrompt, onIdle]);

  const clearTimers = useCallback(() => {
    if (promptTimer.current) clearTimeout(promptTimer.current);
    if (idleTimer.current) clearTimeout(idleTimer.current);
  }, []);

  const resetTimers = useCallback(() => {
    clearTimers();
    startTimers();
    if(isPrompted) {
        setIsPrompted(false);
    }
  }, [clearTimers, startTimers, isPrompted]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

    const handleActivity = () => {
      resetTimers();
    };

    if (isLoggedIn) {
      startTimers();
      events.forEach(event => window.addEventListener(event, handleActivity));
    }

    return () => {
      clearTimers();
      events.forEach(event => window.removeEventListener(event, handleActivity));
    };
  }, [isLoggedIn, resetTimers, startTimers, clearTimers]);
  
  return { isPrompted };
};

export default useInactivityTimer;